'use client'
import { useEffect, useRef, useState, ComponentProps } from "react"
import clsx from "clsx";
import Link from "next/link";

import { cloudinaryUrl } from "@/utils/utils";
import { CardProps } from "./PropertyCard";
import { Icon } from "@/stories/atoms/Icon/Icon";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { MissingImage } from "@/stories/atoms/MissingImage/MissingImage";
import useIsMobile from "@/hooks/useMobile";

import styles from './card-gallery.module.css';

type ImageProps = {
  index: number,
  image: string,
  isSelected: boolean,
  rootRef: Element | Document | null | undefined,
  setIndex: () => void
} & ComponentProps<'img'>

const Image = ({
  image,
  index,
  isSelected,
  rootRef,
  setIndex,
  // carte de 276px au plus, quel que soit l'écran : 1x et 2x suffisent
  sizes = '276px',
  src = cloudinaryUrl({src: image, width: 276, params: ['h_195']}),
  srcSet = `
    ${cloudinaryUrl({src: image, width: 276, params: ['h_195']})} 276w,
    ${cloudinaryUrl({src: image, width: 552, params: ['h_390']})} 552w`,
  ...props
}: ImageProps ) => {
  const ref = useRef<HTMLDivElement>(null)
  // image introuvable ou en erreur : cadre « Pas d'image disponible » à la place de l'icône d'image cassée
  const [broken, setBroken] = useState(false)
  const {isMobile} = useIsMobile()
  const { isIntersecting, ref: imageRef  } = useIntersectionObserver({
    threshold: 0.5,
    root: rootRef,
  });

  // erreur survenue avant l'hydratation (page statique) : onError n'est jamais appelé
  useEffect(() => {
    const img = ref.current?.querySelector('img')
    if (img?.complete && img.naturalWidth === 0) setBroken(true)
  }, [])

  useEffect(() => {
    if (isMobile && isIntersecting) {
      setIndex()
    }
  }, [isIntersecting, setIndex, isMobile])

  useEffect(() => {
    if (isSelected) {
      setTimeout(() => {
        ref?.current?.scrollIntoView({behavior: "smooth", block: "nearest"});
      }, 100)
    }
  }, [ref, isSelected])

  return (
    <div ref={ref} className={styles.imageWpr}>
      {broken ? <MissingImage ref={imageRef}/> : <img
        ref={imageRef}
        className={styles.image}
        src={src}
        sizes={sizes}
        srcSet={srcSet}
        width={276}
        height={195}
        alt=""
        {...props}
        onError={() => setBroken(true)}
        />}
    </div>
  )
}

export const Gallery = ({
  images,
  alt = '',
  imageSizes,
  imageSrcSet,
  url,
  linkLabel,
  loading
}: {
  images: CardProps['images'],
  /** alt de la première image */
  alt?: string,
  imageSizes?: string,
  imageSrcSet?: {height: number, width: number}[],
  url?: string,
  linkLabel?: string,
  loading?: 'lazy' | 'eager'
}) => {
  const [index, setIndex] = useState(0)
  const [active, setActive] = useState(false)
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null)

  const goNext = () => {
    if (!active) {
      setActive(true)
    }
    if (index < (images.length - 1 )) setIndex(index + 1)
  }
  const goPrev = () => {
    if (index > 0) setIndex(index - 1)
  }

  // mêmes images avec ou sans lien : imageSizes / imageSrcSet s'appliquent dans les deux cas
  const imageList = images.map((image, i: number) => (
    <Image
      key={image + i}
      rootRef={rootEl}
      index={i}
      setIndex={() => setIndex(i)}
      image={image}
      {...(imageSrcSet ? {src: cloudinaryUrl({src: image, width: imageSrcSet[0].width, params: [`h_${imageSrcSet[0].height}`]})} : {})}
      {...(imageSrcSet ? {width: imageSrcSet[0].width, height: imageSrcSet[0].height} : {})}
      {...(imageSizes ? {sizes: imageSizes} : {})}
      {...(imageSrcSet ? {
        srcSet: `
        ${imageSrcSet.map(s => `${cloudinaryUrl({src: image, width: s.width, params: [`h_${s.height}`]})} ${s.width}w`).join(',')}`
      } : {})}
      isSelected={i === index && active}
      alt={i === 0 ? alt : ''}
      loading={ i > 0 ? 'lazy' : loading ? loading : 'eager'}/>
  ))

  return (
    <div className={styles.wrapper}>
      <div ref={setRootEl} className={styles.viewport}>
      {images.length > 1 ? (
        <>
          <button type='button' className={clsx(styles.btn, styles.prev)} onClick={goPrev} {...(index === 0 ? {hidden: true} : {})}><span className='sr-only'>Image précédente</span><Icon icon='angle-left' size={'lg'}/></button>
          <button type='button' className={clsx(styles.btn, styles.next)} onClick={goNext} {...(index === images.length -1 ? {hidden: true} : {})}><span className='sr-only'>Image suivant</span><Icon icon='angle-right' size={'lg'}/></button>
        </>
       ) : null }
      {url
        ? <Link className={styles.gallery} href={url} aria-label={linkLabel}>{imageList}</Link>
        : <div className={styles.gallery}>{imageList}</div>}
      </div>
      {images.length > 1 ? (
        <div className={styles.thumbs}>
          { images.map((image, i) => <span key={image} className={clsx(styles.thumb, i === index && styles.thumbActive)}><span className='sr-only'>Aller à l&apos;image {i + 1}</span></span>)}
        </div>
      ) : null}
    </div>
  )
}